export type SoundStatus = 'allowed' | 'blocked' | 'unknown';

// Same switch renderer.ts feeds the player: when false, videos are muted on
// purpose and nothing must offer to unmute them.
export const videoSoundEnabled = import.meta.env.VITE_APP_SOUND_ENABLED?.toLowerCase() !== 'false';

// Safari and every iOS browser (all WebKit underneath) report an Apple vendor.
export const isAppleWebKit = /Apple/.test(navigator.vendor ?? '');

let soundUnlocked = false;
let bootProbe: SoundStatus | null = null;
let probing: Promise<SoundStatus> | null = null;
const listeners = new Set<() => void>();

/**
 * Boot probe, taken before any user activation so it reflects the no-gesture
 * policy: WebKit forwards a click's activation to play() calls made from it,
 * so a probe run from a click would report "allowed" on a blocked page.
 */
export function installSoundProbe(): void {
  void bootStatus().then(notify);
}

export function onSoundChange(fn: () => void): void {
  listeners.add(fn);
}

function notify(): void {
  for (const fn of listeners) fn();
}

function bootStatus(): Promise<SoundStatus> {
  if (bootProbe !== null) return Promise.resolve(bootProbe);
  probing ??= probeSound().then(status => (bootProbe = status));
  return probing;
}

function silentWavUrl(): string {
  const samples = 8;
  const buf = new ArrayBuffer(44 + samples * 2);
  const v = new DataView(buf);
  const str = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(offset + i, s.charCodeAt(i));
  };
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples * 2, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, 8000, true);
  v.setUint32(28, 16000, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, 'data');
  v.setUint32(40, samples * 2, true);
  return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
}

// Silent but unmuted at full volume — exactly what the autoplay policy gates.
// blob: is in the CSP media-src (data: is not).
async function probeSound(): Promise<SoundStatus> {
  const url = silentWavUrl();
  const audio = new Audio(url);
  try {
    await audio.play();
    audio.pause();
    return 'allowed';
  } catch (err) {
    return (err as { name?: string } | null)?.name === 'NotAllowedError' ? 'blocked' : 'unknown';
  } finally {
    audio.removeAttribute('src');
    URL.revokeObjectURL(url);
  }
}

// getAutoplayPolicy is Firefox 112+ only; Chromium relies on userActivation + the boot probe.
type NavigatorExt = Navigator & {
  getAutoplayPolicy?: (type: 'mediaelement') => 'allowed' | 'allowed-muted' | 'disallowed';
  userActivation?: { hasBeenActive: boolean };
};

export async function soundStatus(): Promise<SoundStatus> {
  const nav = navigator as NavigatorExt;
  if (typeof nav.getAutoplayPolicy === 'function') {
    try {
      return nav.getAutoplayPolicy('mediaelement') === 'allowed' ? 'allowed' : 'blocked';
    } catch {
      /* fall through to the heuristics */
    }
  }
  if (soundUnlocked) return 'allowed';
  // Chromium lifts the block for the whole document after any interaction
  // (sticky activation); WebKit gates per media element, so it doesn't count there.
  if (!isAppleWebKit && nav.userActivation?.hasBeenActive) return 'allowed';
  return bootStatus();
}

export function playerMedia(): HTMLMediaElement[] {
  return Array.from(document.querySelectorAll<HTMLMediaElement>('#main-container video, #main-container audio'));
}

export function isPlayerMedia(target: EventTarget | null): target is HTMLMediaElement {
  return target instanceof HTMLMediaElement && !!target.closest('#main-container');
}

/** Something meant to be audible is playing muted (the player's autoplay-block fallback). */
export function mutedPlayback(): boolean {
  return playerMedia().some(el => !el.paused && el.muted && (el instanceof HTMLAudioElement || videoSoundEnabled));
}

/**
 * Must run synchronously inside a click handler (user activation). Unmutes
 * what the autoplay fallback silenced and pokes idle player elements so WebKit
 * (per-element gating) lifts its restriction on them too; an element holding a
 * preloaded item is left alone so it doesn't start early.
 */
export function unlockSound(): void {
  for (const el of playerMedia()) {
    if (!el.paused) {
      if (el.muted && (el instanceof HTMLAudioElement || videoSoundEnabled)) el.muted = false;
    } else if (!el.getAttribute('src')) {
      el.play()?.catch(() => {
        /* no source — expected */
      });
      el.pause();
    }
  }
  soundUnlocked = true;
  notify();
}
